import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Trash2, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const ACCOUNTS = [
  { value: "cash", label: "Cash" },
  { value: "bank", label: "Bank" },
  { value: "pos", label: "POS Terminal" },
  { value: "mobile_money", label: "Mobile Money" },
];

function accountLabel(val: string) {
  return ACCOUNTS.find(a => a.value === val)?.label ?? val;
}

interface FundTransfer {
  id: number;
  fromAccount: string;
  toAccount: string;
  amount: number;
  description: string | null;
  date: string;
  createdAt: string;
}

const EMPTY_FORM = { fromAccount: "", toAccount: "", amount: "", description: "", date: format(new Date(), "yyyy-MM-dd") };

export default function FundTransfers() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const { data: transfers = [], isLoading } = useQuery<FundTransfer[]>({
    queryKey: ["fund-transfers"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/fund-transfers`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  async function handleSave() {
    if (!form.fromAccount || !form.toAccount) {
      toast({ title: "Select both accounts", variant: "destructive" }); return;
    }
    if (form.fromAccount === form.toAccount) {
      toast({ title: "Accounts must be different", variant: "destructive" }); return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast({ title: "Enter a valid amount", variant: "destructive" }); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/fund-transfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, amount: Number(form.amount) }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error);
      }
      await queryClient.invalidateQueries({ queryKey: ["fund-transfers"] });
      toast({ title: "Transfer recorded" });
      setIsOpen(false);
      setForm(EMPTY_FORM);
    } catch (e: any) {
      toast({ title: e.message || "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    setDeleting(id);
    try {
      const res = await fetch(`${BASE}/api/fund-transfers/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Failed to delete transfer");
      }
      await queryClient.invalidateQueries({ queryKey: ["fund-transfers"] });
      toast({ title: "Transfer deleted" });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setDeleting(null);
    }
  }

  const totalMoved = transfers.reduce((s, t) => s + t.amount, 0);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Fund Transfers</h1>
          <p className="text-muted-foreground text-sm mt-1">Move money between cash, bank, POS, and mobile money.</p>
        </div>
        <Button onClick={() => setIsOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" /> New Transfer
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Transferred</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold">{formatCurrency(totalMoved)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Transfers</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-2xl font-bold">{transfers.length}</p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : transfers.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <p className="font-medium">No transfers yet</p>
          <p className="text-sm mt-1">Record your first fund transfer to get started.</p>
        </div>
      ) : (
        <div className="bg-card border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>From</TableHead>
                <TableHead />
                <TableHead>To</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {transfers.map(t => (
                <TableRow key={t.id}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {format(new Date(t.date), "dd MMM yyyy")}
                  </TableCell>
                  <TableCell>
                    <span className="bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 rounded-full">
                      {accountLabel(t.fromAccount)}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <ArrowRight className="w-4 h-4" />
                  </TableCell>
                  <TableCell>
                    <span className="bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-0.5 rounded-full">
                      {accountLabel(t.toAccount)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{t.description ?? "—"}</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(t.amount)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={deleting === t.id}
                      onClick={() => handleDelete(t.id)}
                    >
                      {deleting === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={open => { setIsOpen(open); if (!open) setForm(EMPTY_FORM); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New Fund Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium block mb-1">From Account</label>
              <Select value={form.fromAccount} onValueChange={v => setForm(f => ({ ...f, fromAccount: v }))}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {ACCOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">To Account</label>
              <Select value={form.toAccount} onValueChange={v => setForm(f => ({ ...f, toAccount: v }))}>
                <SelectTrigger><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {ACCOUNTS.filter(a => a.value !== form.fromAccount).map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Amount (₦)</label>
              <Input
                type="number"
                min="1"
                placeholder="0.00"
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Date</label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description (optional)</label>
              <Input
                placeholder="e.g. Cash deposit to GTBank"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setIsOpen(false); setForm(EMPTY_FORM); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
